import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, apiRequest } from '../api/client.js';
import { AlertRender } from './alerts/AlertRender.js';
import { fillPreview } from './alerts/preview.js';
import {
  ALERT_ANIMATIONS,
  ALERT_COLORS,
  ALERT_FONTS,
  ALERT_POSITIONS,
  ALERT_THEMES,
  pickEnum,
} from './alerts/style-values.js';
import type {
  AlertDefinition,
  AlertInstanceHistory,
  AlertOutput,
  AlertOutputTokenCreated,
  AlertTrigger,
  ConnaroEventType,
  PlatformName,
} from '../api/types.js';
import { Alert } from '../components/Alert.js';
import { EmptyState } from '../components/EmptyState.js';
import { useSession } from '../auth/session-context.js';
import './alerts.css';
import { platformLabel } from '../platforms/labels.js';

/**
 * Connaro Alerts, in the Console (ARCHITECTURE.md §99, §100; Alerts brief).
 *
 * The trigger and platform pickers below are driven entirely by
 * `GET /api/v1/alerts/triggers` — the §99 capability matrix, projected. There
 * is no second list of triggers or platforms typed into this file: that is
 * exactly the drift §99 exists to prevent, and it is why `platformLabel`
 * below only ever supplies a NAME for a platform the server already offered,
 * never decides which platforms exist.
 *
 * THREE THINGS THIS PAGE DELIBERATELY DOES NOT DO:
 *
 *   - Offer a "revoke token" control. There is no route to LIST an output's
 *     tokens, so an operator can never rediscover a `tokenId` to revoke —
 *     building that control would be a button with no way to populate it.
 *     Token issuance here is create-and-show-once, matching what the API can
 *     actually support.
 *
 *   - Let an operator choose which output a test alert fires at.
 *     `POST .../test` always renders against the tenant's first output, so a
 *     picker here would imply a choice that does not exist.
 *
 *   - Redisplay a token's plaintext. It is held in this component's state for
 *     exactly as long as this page stays mounted with nothing else changing
 *     it, and nowhere else — a token the Console could show twice is a token
 *     stored in recoverable form, which is the entire point of hashing it at
 *     rest.
 */

const EVENT_LABEL: Record<ConnaroEventType, string> = {
  'chat.message': 'Chat message',
  'chat.deleted': 'Message deleted',
  'user.joined': 'Viewer joined',
  follow: 'Follow',
  subscription: 'Subscription',
  membership: 'Membership',
  gift: 'Gift',
  donation: 'Donation',
  raid: 'Raid',
  system: 'System',
};

/*
 * Appearance vocabularies come from `style-values.ts`, not a list typed
 * here — the same discipline `platformLabel` and `EVENT_LABEL` already
 * follow, and the reason `pickEnum` exists at all (see that file's doc
 * comment). Labels below only ever supply a NAME for a value the allowlist
 * already offers.
 */
type AlertColorChoice = (typeof ALERT_COLORS)[number];
type AlertThemeChoice = (typeof ALERT_THEMES)[number];
type AlertFontChoice = (typeof ALERT_FONTS)[number];
type AlertAnimationChoice = (typeof ALERT_ANIMATIONS)[number];
type AlertPositionChoice = (typeof ALERT_POSITIONS)[number];

const COLOR_LABEL: Record<AlertColorChoice, string> = {
  white: 'White',
  amber: 'Amber',
  green: 'Green',
  cyan: 'Cyan',
  violet: 'Violet',
  pink: 'Pink',
  red: 'Red',
};

const THEME_LABEL: Record<AlertThemeChoice, string> = {
  minimal: 'Minimal',
  dark: 'Dark',
  light: 'Light',
  neon: 'Neon',
};

const FONT_LABEL: Record<AlertFontChoice, string> = {
  system: 'System',
  sans: 'Sans-serif',
  serif: 'Serif',
  mono: 'Monospace',
  wide: 'Wide',
};

const ANIMATION_LABEL: Record<AlertAnimationChoice, string> = {
  none: 'None',
  fade: 'Fade',
  slide: 'Slide',
  pop: 'Pop',
};

/* British prose: "Centre", never "Center". */
const POSITION_LABEL: Record<AlertPositionChoice, string> = {
  'top-left': 'Top left',
  'top-center': 'Top centre',
  'top-right': 'Top right',
  'middle-left': 'Middle left',
  center: 'Centre',
  'middle-right': 'Middle right',
  'bottom-left': 'Bottom left',
  'bottom-center': 'Bottom centre',
  'bottom-right': 'Bottom right',
};

/*
 * The Console's own default for a NEW alert's animation is 'fade' — the
 * schema's UNSET default stays 'none' because that is what an empty `{}`
 * column means (see `alertStyleSchema`'s own comment on this exact split).
 * Every other appearance default below matches what `{}` already resolves
 * to, so a freshly created alert looks exactly like the seven fields an
 * operator has not touched yet.
 */
const CREATE_STYLE_DEFAULTS = {
  theme: 'minimal' as AlertThemeChoice,
  font: 'system' as AlertFontChoice,
  fontScale: 1,
  animation: 'fade' as AlertAnimationChoice,
  textOutline: false,
  textShadow: true,
  backgroundOpacity: 0.82,
  cornerRadius: 12,
  color: 'white' as AlertColorChoice,
};

const DEFAULT_DURATION_MS = 5_000;
const DEFAULT_COOLDOWN_SECONDS = 0;

type Notice = { tone: 'success' | 'error'; text: string };

function overlayUrl(outputId: string, token?: string): string {
  const base = `${window.location.origin}/overlay/alerts/${outputId}`;
  return token === undefined ? base : `${base}?k=${token}`;
}

export function AlertsPage(): React.JSX.Element {
  const { can } = useSession();
  const mayManage = can('alerts.manage');

  const [triggers, setTriggers] = useState<AlertTrigger[] | null>(null);
  const [definitions, setDefinitions] = useState<AlertDefinition[] | null>(null);
  const [outputs, setOutputs] = useState<AlertOutput[] | null>(null);
  const [instances, setInstances] = useState<AlertInstanceHistory[] | null>(null);

  const [entitled, setEntitled] = useState(true);
  const [permitted, setPermitted] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // --- create/edit definition form ------------------------------------------
  /*
   * One form, two modes — not a second form that drifts from the first.
   * `editing === null` is "Add an alert" and a POST; a definition here is
   * "Edit alert" and a PATCH. Every field below is shared by both.
   */
  const [editing, setEditing] = useState<AlertDefinition | null>(null);
  const [newName, setNewName] = useState('');
  const [newEventType, setNewEventType] = useState<ConnaroEventType | ''>('');
  const [newPlatforms, setNewPlatforms] = useState<PlatformName[]>([]);
  const [newTemplate, setNewTemplate] = useState('');

  // --- appearance (feeds the .apreview block below, and PATCH/POST's `style`) ----
  const [newColor, setNewColor] = useState<AlertColorChoice>(CREATE_STYLE_DEFAULTS.color);
  const [newTheme, setNewTheme] = useState<AlertThemeChoice>(CREATE_STYLE_DEFAULTS.theme);
  const [newFont, setNewFont] = useState<AlertFontChoice>(CREATE_STYLE_DEFAULTS.font);
  const [newFontScale, setNewFontScale] = useState(CREATE_STYLE_DEFAULTS.fontScale);
  const [newAnimation, setNewAnimation] = useState<AlertAnimationChoice>(
    CREATE_STYLE_DEFAULTS.animation,
  );
  const [newTextOutline, setNewTextOutline] = useState(CREATE_STYLE_DEFAULTS.textOutline);
  const [newTextShadow, setNewTextShadow] = useState(CREATE_STYLE_DEFAULTS.textShadow);
  const [newBackgroundOpacity, setNewBackgroundOpacity] = useState(
    CREATE_STYLE_DEFAULTS.backgroundOpacity,
  );
  const [newCornerRadius, setNewCornerRadius] = useState(CREATE_STYLE_DEFAULTS.cornerRadius);

  /*
   * A preview surface, never part of the alert. Widgets settled this: the
   * backdrop helps an operator judge legibility and must not reach the saved
   * configuration, because one baked in would put an opaque rectangle on a
   * broadcast.
   */
  const [previewOnScene, setPreviewOnScene] = useState(false);
  const [creating, setCreating] = useState(false);

  // --- token create-and-show-once -----------------------------------------
  const [tokenLabels, setTokenLabels] = useState<Record<string, string>>({});
  const [tokenBusyOutputId, setTokenBusyOutputId] = useState<string | null>(null);
  const [revealedToken, setRevealedToken] = useState<{
    outputId: string;
    label: string;
    value: string;
  } | null>(null);

  // --- overlay layout (anchor picker) ---------------------------------------
  const [layoutBusyOutputId, setLayoutBusyOutputId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [triggersRes, definitionsRes, outputsRes, instancesRes] = await Promise.all([
        apiRequest<{ triggers: AlertTrigger[] }>('/alerts/triggers'),
        apiRequest<{ definitions: AlertDefinition[] }>('/alerts/definitions'),
        apiRequest<{ outputs: AlertOutput[] }>('/alerts/outputs'),
        apiRequest<{ instances: AlertInstanceHistory[] }>('/alerts/instances'),
      ]);

      setTriggers(triggersRes.triggers);
      setDefinitions(definitionsRes.definitions);
      setOutputs(outputsRes.outputs);
      setInstances(instancesRes.instances);
      setError(null);
    } catch (cause) {
      /*
       * 402 vs 403, kept distinct end to end (Alerts brief).
       *
       * ENTITLEMENT_REQUIRED means "not on this plan" — the fix is to
       * upgrade. PERMISSION_DENIED/FORBIDDEN means "your role does not
       * include this" — upgrading the plan would not help, and telling an
       * operator to upgrade when the real answer is "ask an admin" sends
       * them to pay for something that was never the problem.
       */
      if (cause instanceof ApiError && cause.code === 'ENTITLEMENT_REQUIRED') {
        setEntitled(false);
        return;
      }
      if (cause instanceof ApiError && (cause.code === 'PERMISSION_DENIED' || cause.code === 'FORBIDDEN')) {
        setPermitted(false);
        return;
      }
      setDefinitions([]);
      setOutputs([]);
      setInstances([]);
      setError(cause instanceof ApiError ? cause.message : 'Connaro could not load Alerts.');
    }
  }, []);

  const refreshInstances = useCallback(async () => {
    try {
      const data = await apiRequest<{ instances: AlertInstanceHistory[] }>('/alerts/instances');
      setInstances(data.instances);
    } catch {
      // History is a convenience refresh after a test fire; a failure here
      // must not hide the success notice the caller already set.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const platformsForTrigger = useMemo<PlatformName[]>(
    () => triggers?.find((entry) => entry.eventType === newEventType)?.platforms ?? [],
    [triggers, newEventType],
  );

  /** Everything the form needs to go back to "Add an alert", blank. */
  const resetForm = useCallback(() => {
    setEditing(null);
    setNewName('');
    setNewEventType('');
    setNewPlatforms([]);
    setNewTemplate('');
    setNewColor(CREATE_STYLE_DEFAULTS.color);
    setNewTheme(CREATE_STYLE_DEFAULTS.theme);
    setNewFont(CREATE_STYLE_DEFAULTS.font);
    setNewFontScale(CREATE_STYLE_DEFAULTS.fontScale);
    setNewAnimation(CREATE_STYLE_DEFAULTS.animation);
    setNewTextOutline(CREATE_STYLE_DEFAULTS.textOutline);
    setNewTextShadow(CREATE_STYLE_DEFAULTS.textShadow);
    setNewBackgroundOpacity(CREATE_STYLE_DEFAULTS.backgroundOpacity);
    setNewCornerRadius(CREATE_STYLE_DEFAULTS.cornerRadius);
  }, []);

  /**
   * Load an existing definition into the same form the "Add an alert"
   * section renders, and switch it into edit mode.
   *
   * Every appearance field is read defensively — `pickEnum` and a `typeof`
   * fallback, exactly like `AlertRender` — because `definition.style` is the
   * same untrusted JSON column that renderer reads. An edit form that threw
   * on a corrupted style value would make that row impossible to fix.
   *
   * `?? {}` guards against version skew, the same way `queue-client.ts`
   * guards `payload.layout` — a Console newer than the API it is talking to
   * can receive a `definition` from before `style` existed on the wire.
   */
  const startEditing = useCallback((definition: AlertDefinition) => {
    setEditing(definition);
    setNewName(definition.name);
    setNewEventType(definition.eventType);
    setNewPlatforms(definition.platforms);
    setNewTemplate(definition.template);

    const style = definition.style ?? {};
    setNewColor(pickEnum(style.color, ALERT_COLORS, CREATE_STYLE_DEFAULTS.color));
    setNewTheme(pickEnum(style.theme, ALERT_THEMES, CREATE_STYLE_DEFAULTS.theme));
    setNewFont(pickEnum(style.font, ALERT_FONTS, CREATE_STYLE_DEFAULTS.font));
    setNewFontScale(typeof style.fontScale === 'number' ? style.fontScale : 1);
    // Unlike the CREATE default, an EDITED alert's unset animation is 'none'
    // — what `{}` actually renders as — not the Console's own 'fade' pick
    // for something brand new (see `CREATE_STYLE_DEFAULTS`'s comment).
    setNewAnimation(pickEnum(style.animation, ALERT_ANIMATIONS, 'none'));
    setNewTextOutline(style.textOutline === true);
    setNewTextShadow(style.textShadow !== false);
    setNewBackgroundOpacity(
      typeof style.backgroundOpacity === 'number' ? style.backgroundOpacity : 0.82,
    );
    setNewCornerRadius(typeof style.cornerRadius === 'number' ? style.cornerRadius : 12);
  }, []);

  const cancelEditing = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const submitDefinition = useCallback(
    async (event: React.SyntheticEvent): Promise<void> => {
      event.preventDefault();
      if (newEventType === '') return;

      const style = {
        color: newColor,
        theme: newTheme,
        font: newFont,
        fontScale: newFontScale,
        animation: newAnimation,
        textOutline: newTextOutline,
        textShadow: newTextShadow,
        backgroundOpacity: newBackgroundOpacity,
        cornerRadius: newCornerRadius,
      };

      setCreating(true);
      try {
        if (editing === null) {
          const { definition } = await apiRequest<{ definition: AlertDefinition }>(
            '/alerts/definitions',
            {
              method: 'POST',
              body: {
                name: newName,
                eventType: newEventType,
                platforms: newPlatforms,
                template: newTemplate,
                durationMs: DEFAULT_DURATION_MS,
                cooldownSeconds: DEFAULT_COOLDOWN_SECONDS,
                style,
              },
            },
          );

          setDefinitions((current) => [definition, ...(current ?? [])]);
          setNotice({ tone: 'success', text: `${definition.name} added.` });
        } else {
          const { definition } = await apiRequest<{ definition: AlertDefinition }>(
            `/alerts/definitions/${editing.id}`,
            {
              method: 'PATCH',
              body: {
                name: newName,
                eventType: newEventType,
                platforms: newPlatforms,
                template: newTemplate,
                style,
              },
            },
          );

          setDefinitions(
            (current) =>
              current?.map((entry) => (entry.id === definition.id ? definition : entry)) ??
              current,
          );
          setNotice({ tone: 'success', text: `${definition.name} updated.` });
        }

        resetForm();
      } catch (cause) {
        setNotice({
          tone: 'error',
          text:
            cause instanceof ApiError
              ? cause.message
              : `Connaro could not ${editing === null ? 'add' : 'update'} that alert.`,
        });
      } finally {
        setCreating(false);
      }
    },
    [
      editing,
      newEventType,
      newName,
      newPlatforms,
      newTemplate,
      newColor,
      newTheme,
      newFont,
      newFontScale,
      newAnimation,
      newTextOutline,
      newTextShadow,
      newBackgroundOpacity,
      newCornerRadius,
      resetForm,
    ],
  );

  const toggleDefinition = useCallback(async (definition: AlertDefinition) => {
    setBusyId(definition.id);
    try {
      const { definition: updated } = await apiRequest<{ definition: AlertDefinition }>(
        `/alerts/definitions/${definition.id}`,
        { method: 'PATCH', body: { enabled: !definition.enabled } },
      );
      setDefinitions(
        (current) => current?.map((entry) => (entry.id === updated.id ? updated : entry)) ?? current,
      );
    } catch (cause) {
      setNotice({
        tone: 'error',
        text: cause instanceof ApiError ? cause.message : 'Connaro could not update that alert.',
      });
    } finally {
      setBusyId(null);
    }
  }, []);

  const removeDefinition = useCallback(async (definition: AlertDefinition) => {
    setBusyId(definition.id);
    try {
      await apiRequest(`/alerts/definitions/${definition.id}`, { method: 'DELETE' });
      setDefinitions((current) => current?.filter((entry) => entry.id !== definition.id) ?? current);
    } catch (cause) {
      setNotice({
        tone: 'error',
        text: cause instanceof ApiError ? cause.message : 'Connaro could not remove that alert.',
      });
    } finally {
      setBusyId(null);
    }
  }, []);

  const testDefinition = useCallback(
    async (definition: AlertDefinition) => {
      setBusyId(definition.id);
      try {
        await apiRequest(`/alerts/definitions/${definition.id}/test`, { method: 'POST' });
        // Every definition tests against the tenant's FIRST output — never a
        // choice this page offers (see the module doc above).
        setNotice({
          tone: 'success',
          text: `Test alert queued for "${definition.name}" — synthetic, not a real event.`,
        });
        await refreshInstances();
      } catch (cause) {
        setNotice({
          tone: 'error',
          text: cause instanceof ApiError ? cause.message : 'Connaro could not send a test alert.',
        });
      } finally {
        setBusyId(null);
      }
    },
    [refreshInstances],
  );

  const createToken = useCallback(async (output: AlertOutput) => {
    setTokenBusyOutputId(output.id);
    setRevealedToken(null);
    try {
      const trimmedLabel = tokenLabels[output.id]?.trim() ?? '';
      const label = trimmedLabel === '' ? 'Overlay token' : trimmedLabel;
      const created = await apiRequest<AlertOutputTokenCreated>(
        `/alerts/outputs/${output.id}/tokens`,
        { method: 'POST', body: { label } },
      );
      // `created.token` is the plaintext, present on THIS response only (see
      // the module doc above) — held in state, and nowhere else.
      setRevealedToken({ outputId: output.id, label: created.label, value: created.token });
    } catch (cause) {
      setNotice({
        tone: 'error',
        text: cause instanceof ApiError ? cause.message : 'Connaro could not create a token.',
      });
    } finally {
      setTokenBusyOutputId(null);
    }
  }, [tokenLabels]);

  /**
   * Move where THIS output's alerts render (§103.3's nine-point anchor).
   *
   * Sends only `{ layout: { position } }` — never the whole layout object —
   * because `PATCH /alerts/outputs/:id` merges a layout patch field-by-field
   * onto what is already stored (Task 5). Sending the whole object here
   * would risk clobbering a `maxWidthPercent` this page has no control for
   * yet with whatever stale value happened to be in state.
   */
  const setOutputPosition = useCallback(
    async (output: AlertOutput, position: AlertPositionChoice): Promise<void> => {
      setLayoutBusyOutputId(output.id);
      try {
        const { output: updated } = await apiRequest<{ output: AlertOutput }>(
          `/alerts/outputs/${output.id}`,
          { method: 'PATCH', body: { layout: { position } } },
        );
        setOutputs(
          (current) => current?.map((entry) => (entry.id === updated.id ? updated : entry)) ?? current,
        );
      } catch (cause) {
        setNotice({
          tone: 'error',
          text:
            cause instanceof ApiError ? cause.message : 'Connaro could not move that overlay.',
        });
      } finally {
        setLayoutBusyOutputId(null);
      }
    },
    [],
  );

  if (!entitled) {
    return (
      <section className="panel" aria-labelledby="alerts-heading">
        <div className="panel__header">
          <h1 className="panel__title" id="alerts-heading">
            Connaro Alerts
          </h1>
        </div>
        <div className="panel__body">
          <EmptyState title="Not included in this plan">
            <p>
              Connaro Alerts renders on-screen notifications for follows, subscriptions, gifts and
              more, over a browser source your streaming software already supports. It is included
              from the Team plan upwards.
            </p>
          </EmptyState>
        </div>
      </section>
    );
  }

  if (!permitted) {
    return (
      <section className="panel" aria-labelledby="alerts-heading">
        <div className="panel__header">
          <h1 className="panel__title" id="alerts-heading">
            Connaro Alerts
          </h1>
        </div>
        <div className="panel__body">
          <EmptyState title="Your role does not include Alerts">
            <p>An owner or admin on this workspace can grant access to Alerts.</p>
          </EmptyState>
        </div>
      </section>
    );
  }

  return (
    <>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {notice === null ? null : <Alert tone={notice.tone}>{notice.text}</Alert>}

      <section className="panel" aria-labelledby="alerts-heading">
        <div className="panel__header">
          <h1 className="panel__title" id="alerts-heading">
            Connaro Alerts
          </h1>
        </div>
        <div className="panel__body">
          <p className="page__hint">
            An alert fires on real events from a connected platform. Every trigger and platform
            offered below is one Connaro can actually deliver today — nothing here is aspirational.
          </p>

          {definitions === null ? (
            <p>Loading…</p>
          ) : definitions.length === 0 ? (
            <EmptyState title="No alerts yet">
              <p>Add one below to start showing something on your overlay.</p>
            </EmptyState>
          ) : (
            <ul className="alerts__list">
              {definitions.map((definition) => (
                <li key={definition.id} className="alerts__row">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={definition.enabled}
                    aria-label={definition.name}
                    className="switch"
                    disabled={!mayManage || busyId === definition.id}
                    onClick={() => {
                      void toggleDefinition(definition);
                    }}
                  >
                    <span className="switch__thumb" aria-hidden="true" />
                  </button>

                  <span className="alerts__def-name">{definition.name}</span>
                  <span className="alerts__def-trigger">{EVENT_LABEL[definition.eventType]}</span>
                  <span className="alerts__def-platforms">
                    {definition.platforms.length === 0
                      ? 'Any platform that can trigger this'
                      : definition.platforms.map((platform) => platformLabel(platform)).join(', ')}
                  </span>

                  <span className="alerts__row-actions">
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={busyId === definition.id}
                      onClick={() => {
                        void testDefinition(definition);
                      }}
                    >
                      Test
                    </button>
                    {mayManage ? (
                      <button
                        type="button"
                        className="btn btn--sm"
                        aria-label={`Edit ${definition.name}`}
                        disabled={busyId === definition.id}
                        onClick={() => {
                          startEditing(definition);
                        }}
                      >
                        Edit
                      </button>
                    ) : null}
                    {mayManage ? (
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        disabled={busyId === definition.id}
                        onClick={() => {
                          void removeDefinition(definition);
                        }}
                      >
                        Remove
                      </button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {mayManage ? (
        <section className="panel alerts__section" aria-labelledby="alerts-add-heading">
          <div className="panel__header">
            <h2 className="panel__title" id="alerts-add-heading">
              {editing === null ? 'Add an alert' : 'Edit alert'}
            </h2>
          </div>
          <div className="panel__body">
            <form
              onSubmit={(event) => {
                void submitDefinition(event);
              }}
            >
              <div className="field">
                <label className="field__label" htmlFor="alert-name">
                  Name
                </label>
                <input
                  id="alert-name"
                  className="field__input"
                  value={newName}
                  required
                  onChange={(event) => {
                    setNewName(event.target.value);
                  }}
                />
              </div>

              <div className="field">
                <label className="field__label" htmlFor="alert-trigger">
                  Trigger
                </label>
                {/*
                 * Options come from `triggers` alone — the §99 matrix, as the
                 * server returns it. A trigger nothing can emit is simply
                 * absent from this list; it is never rendered disabled, which
                 * would still be a promise the product cannot keep.
                 */}
                <select
                  id="alert-trigger"
                  className="field__input"
                  value={newEventType}
                  required
                  onChange={(event) => {
                    setNewEventType(event.target.value as ConnaroEventType);
                    setNewPlatforms([]);
                  }}
                >
                  <option value="" disabled>
                    Choose a trigger…
                  </option>
                  {(triggers ?? []).map((trigger) => (
                    <option key={trigger.eventType} value={trigger.eventType}>
                      {EVENT_LABEL[trigger.eventType]}
                    </option>
                  ))}
                </select>
              </div>

              <fieldset className="field alerts__platforms">
                <legend className="field__label">Platforms</legend>
                {/*
                 * Leaving every box unchecked means "any platform that can
                 * trigger this event" — not "no platforms". The checkboxes
                 * narrow an already-honest default; they never widen it past
                 * what `platformsForTrigger` says is possible.
                 */}
                {platformsForTrigger.length === 0 ? (
                  <p className="field__hint">Choose a trigger to see which platforms can send it.</p>
                ) : (
                  platformsForTrigger.map((platform) => (
                    <label key={platform} className="alerts__platform-choice">
                      <input
                        type="checkbox"
                        checked={newPlatforms.includes(platform)}
                        onChange={(event) => {
                          setNewPlatforms((current) =>
                            event.target.checked
                              ? [...current, platform]
                              : current.filter((entry) => entry !== platform),
                          );
                        }}
                      />{' '}
                      {platformLabel(platform)}
                    </label>
                  ))
                )}
              </fieldset>

              <div className="field">
                <label className="field__label" htmlFor="alert-template">
                  What it says
                </label>
                <input
                  id="alert-template"
                  className="field__input"
                  value={newTemplate}
                  required
                  placeholder="{displayName} just followed!"
                  onChange={(event) => {
                    setNewTemplate(event.target.value);
                  }}
                />
                <p className="field__hint">
                  Use {'{username}'}, {'{displayName}'}, {'{platform}'}, {'{amount}'} or {'{text}'} —
                  Connaro fills each in from the real event. A line of celebration, not an essay.
                </p>
              </div>

              {/*
                * ==================== APPEARANCE ====================
                * Not behind a disclosure. The defect this redesign fixes is
                * that appearance was invisible; hiding these controls one
                * click deep would reproduce that in miniature. Every value
                * chosen here is untrusted JSON on the way back OUT of the
                * database — see `AlertRender`'s own doc comment — but on the
                * way IN it is a closed radio/checkbox/range choice, so
                * nothing arbitrary can reach the `style` column from here.
                * =====================================================
                */}
              <h3 className="alerts__appearance-heading">Appearance</h3>
              <div className="astyle">
                <fieldset className="field appearance__fieldset">
                  <legend className="field__label">Colour</legend>
                  {/*
                   * Never colour alone: each swatch carries its own NAME as
                   * text, so choosing — and later reading back what was
                   * chosen — never depends on distinguishing a hue.
                   */}
                  <div className="acolor-grid">
                    {ALERT_COLORS.map((color) => (
                      <label key={color} className="acolor-choice">
                        <input
                          type="radio"
                          name="alert-color"
                          value={color}
                          checked={newColor === color}
                          onChange={() => {
                            setNewColor(color);
                          }}
                        />
                        <span className={`acolor-swatch acolor-swatch--${color}`} aria-hidden="true" />
                        <span>{COLOR_LABEL[color]}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="field appearance__fieldset">
                  <legend className="field__label">Theme</legend>
                  {ALERT_THEMES.map((theme) => (
                    <label key={theme} className="appearance__choice">
                      <input
                        type="radio"
                        name="alert-theme"
                        value={theme}
                        checked={newTheme === theme}
                        onChange={() => {
                          setNewTheme(theme);
                        }}
                      />
                      <span>{THEME_LABEL[theme]}</span>
                    </label>
                  ))}
                </fieldset>

                <fieldset className="field appearance__fieldset">
                  <legend className="field__label">Font</legend>
                  {ALERT_FONTS.map((font) => (
                    <label key={font} className="appearance__choice">
                      <input
                        type="radio"
                        name="alert-font"
                        value={font}
                        checked={newFont === font}
                        onChange={() => {
                          setNewFont(font);
                        }}
                      />
                      <span>{FONT_LABEL[font]}</span>
                    </label>
                  ))}
                </fieldset>

                <fieldset className="field appearance__fieldset">
                  <legend className="field__label">Animation</legend>
                  {ALERT_ANIMATIONS.map((animation) => (
                    <label key={animation} className="appearance__choice">
                      <input
                        type="radio"
                        name="alert-animation"
                        value={animation}
                        checked={newAnimation === animation}
                        onChange={() => {
                          setNewAnimation(animation);
                        }}
                      />
                      <span>{ANIMATION_LABEL[animation]}</span>
                    </label>
                  ))}
                </fieldset>

                <div className="field">
                  <label className="field__label" htmlFor="alert-size">
                    Size
                  </label>
                  <div className="appearance__row">
                    <input
                      id="alert-size"
                      type="range"
                      min={0.5}
                      max={3}
                      step={0.1}
                      value={newFontScale}
                      onChange={(event) => {
                        setNewFontScale(Number(event.target.value));
                      }}
                    />
                    <output className="appearance__value" htmlFor="alert-size">
                      {newFontScale.toFixed(1)}×
                    </output>
                  </div>
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="alert-opacity">
                    Background opacity
                  </label>
                  <div className="appearance__row">
                    <input
                      id="alert-opacity"
                      type="range"
                      min={0}
                      max={1}
                      step={0.01}
                      value={newBackgroundOpacity}
                      onChange={(event) => {
                        setNewBackgroundOpacity(Number(event.target.value));
                      }}
                    />
                    <output className="appearance__value" htmlFor="alert-opacity">
                      {Math.round(newBackgroundOpacity * 100)}%
                    </output>
                  </div>
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="alert-radius">
                    Corner radius
                  </label>
                  <div className="appearance__row">
                    <input
                      id="alert-radius"
                      type="range"
                      min={0}
                      max={32}
                      step={1}
                      value={newCornerRadius}
                      onChange={(event) => {
                        setNewCornerRadius(Number(event.target.value));
                      }}
                    />
                    <output className="appearance__value" htmlFor="alert-radius">
                      {newCornerRadius}px
                    </output>
                  </div>
                </div>

                <fieldset className="field appearance__fieldset">
                  <legend className="field__label">Text effects</legend>
                  <label className="appearance__choice">
                    <input
                      type="checkbox"
                      checked={newTextOutline}
                      onChange={(event) => {
                        setNewTextOutline(event.target.checked);
                      }}
                    />
                    <span>Outline</span>
                  </label>
                  <label className="appearance__choice">
                    <input
                      type="checkbox"
                      checked={newTextShadow}
                      onChange={(event) => {
                        setNewTextShadow(event.target.checked);
                      }}
                    />
                    <span>Shadow</span>
                  </label>
                </fieldset>
              </div>

              {/*
                * ==================== THE PREVIEW ====================
                * §26 of the brief, and the audit's verdict on this surface:
                * an alert's appearance IS the product, and it was configured
                * blind. An operator wrote `{displayName} just followed!` into
                * a bare input and found out what it produced when it appeared
                * on their broadcast.
                *
                * This is the SAME component the overlay draws with, over the
                * same backdrop question Widgets settled: an alert is
                * composited over gameplay, so judging it on the Console's own
                * surface judges it against a background it will never have.
                * Its style prop comes straight from the controls above, so
                * this is also where an operator sees their appearance
                * choices actually take effect, rather than trusting a
                * setting they cannot see.
                * =====================================================
                */}
                <div className="apreview">
                  <div className="apreview__head">
                    <span className="apreview__label">Preview</span>
                    {/*
                      * The NAME is stable and `aria-pressed` carries the state.
                      * This button previously relabelled itself between "On
                      * transparent" and "On gameplay" while ALSO setting
                      * aria-pressed, which announced as "On transparent, toggle
                      * button, not pressed" — a name describing the state, then
                      * the state again, disagreeing. Sighted readers had the
                      * same ambiguity beside a heading: which half was the
                      * current backdrop and which was the action?
                      */}
                    <button
                      type="button"
                      className="apreview__backdrop-toggle"
                      aria-pressed={previewOnScene}
                      onClick={() => {
                        setPreviewOnScene((current) => !current);
                      }}
                    >
                      On gameplay
                    </button>
                  </div>

                  <div
                    className={`apreview__stage${previewOnScene ? ' apreview__stage--scene' : ''}`}
                  >
                    <AlertRender
                      text={
                        newTemplate.trim() === ''
                          ? fillPreview('{displayName} just followed!')
                          : fillPreview(newTemplate)
                      }
                      style={{
                        color: newColor,
                        theme: newTheme,
                        font: newFont,
                        fontScale: newFontScale,
                        animation: newAnimation,
                        textOutline: newTextOutline,
                        textShadow: newTextShadow,
                        backgroundOpacity: newBackgroundOpacity,
                        cornerRadius: newCornerRadius,
                      }}
                    />
                  </div>

                  <p className="field__hint">
                    Example values. Your real events replace them.
                  </p>
                </div>

              <div className="alerts__form-actions">
                <button type="submit" className="btn btn--primary" disabled={creating}>
                  {editing === null ? 'Add alert' : 'Save changes'}
                </button>
                {editing === null ? null : (
                  <button type="button" className="btn" onClick={cancelEditing}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </section>
      ) : null}

      <section className="panel alerts__section" aria-labelledby="alerts-outputs-heading">
        <div className="panel__header">
          <h2 className="panel__title" id="alerts-outputs-heading">
            Overlay
          </h2>
        </div>
        <div className="panel__body">
          <p className="page__hint">
            Add the overlay URL as a Browser Source in your streaming software. It renders alerts
            only — nothing else from Connaro.
          </p>

          {outputs === null ? (
            <p>Loading…</p>
          ) : (
            <ul className="alerts__list">
              {outputs.map((output) => (
                <li key={output.id} className="alerts__row alerts__row--output">
                  <div>
                    <strong>{output.name}</strong>
                    <p className="field__hint">Overlay URL: {overlayUrl(output.id)}</p>
                  </div>

                  {/*
                   * §103.3's nine-point anchor, as REAL radios with REAL
                   * labels — never nine divs whose selection is a highlight.
                   * Position must not be communicated by appearance alone.
                   */}
                  <fieldset className="field aanchor">
                    <legend className="field__label">Where alerts appear</legend>
                    <div className="aanchor__grid">
                      {ALERT_POSITIONS.map((position) => {
                        const checked =
                          pickEnum(output.layout?.position, ALERT_POSITIONS, 'center') ===
                          position;
                        return (
                          <label
                            key={position}
                            className={`aanchor__choice${checked ? ' aanchor__choice--selected' : ''}`}
                          >
                            <input
                              type="radio"
                              name={`output-position-${output.id}`}
                              value={position}
                              checked={checked}
                              disabled={!mayManage || layoutBusyOutputId === output.id}
                              onChange={() => {
                                void setOutputPosition(output, position);
                              }}
                            />
                            <span>{POSITION_LABEL[position]}</span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>

                  {mayManage ? (
                    <div className="alerts__token-form">
                      <label className="field__label" htmlFor={`token-label-${output.id}`}>
                        Token label
                      </label>
                      <input
                        id={`token-label-${output.id}`}
                        className="field__input"
                        value={tokenLabels[output.id] ?? ''}
                        placeholder="Overlay token"
                        onChange={(event) => {
                          setTokenLabels((current) => ({ ...current, [output.id]: event.target.value }));
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn--sm"
                        disabled={tokenBusyOutputId === output.id}
                        onClick={() => {
                          void createToken(output);
                        }}
                      >
                        Create token
                      </button>
                    </div>
                  ) : null}

                  {revealedToken !== null && revealedToken.outputId === output.id ? (
                    <div className="callout" role="status">
                      <p className="field__hint">
                        Copy this now — Connaro will not show this token again.
                      </p>
                      <p>
                        Token: <code className="alerts__token-value">{revealedToken.value}</code>
                      </p>
                      <p className="field__hint">
                        Full overlay URL: {overlayUrl(output.id, revealedToken.value)}
                      </p>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="panel alerts__section" aria-labelledby="alerts-history-heading">
        <div className="panel__header">
          <h2 className="panel__title" id="alerts-history-heading">
            History
          </h2>
        </div>
        <div className="panel__body">
          {instances === null ? (
            <p>Loading…</p>
          ) : instances.length === 0 ? (
            <EmptyState title="Nothing has fired yet">
              <p>Alerts that render on your overlay will appear here, including test alerts.</p>
            </EmptyState>
          ) : (
            <ul className="alerts__history">
              {instances.map((instance) => (
                <li key={instance.id} className="alerts__history-row">
                  {/*
                   * Untrusted, chat-derived text (§21, §57). React text
                   * interpolation only — never `dangerouslySetInnerHTML`.
                   */}
                  <p className="alerts__history-text">{instance.text}</p>
                  <p className="field__hint">
                    {instance.status.toLowerCase()}
                    {instance.synthetic ? ' · synthetic' : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}
